import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  TextField, MenuItem, Typography
} from "@mui/material";
import { Add, Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useAICallCampaigns, useMarketingPresets, useInterval, formatBdt, formatNumber, dhaka, presetLabel, CAMPAIGN_STATUS
} from "./useAICalling";
import { AICallingPage, Loading, ErrorState, CampaignStatusChip } from "./AICallingComponents";
import AICallCampaignForm from "./AICallCampaignForm";

const audienceText = (c, presets) => {
  if (c.system === "ladder") return "Automatic (channel ladder)";
  if (c.system === "notice") return "Automatic (purchase notice)";
  if (c.system === "signup") return "Automatic (new signups)";
  if (c.audience?.preset) return presetLabel(presets, c.audience.preset);
  const n = c.audience?.selectedUserIds?.length || 0;
  return n ? `${formatNumber(n)} picked` : "—";
};

const AICallingCampaigns = () => {
  const navigate = useNavigate();
  const { campaigns, loading, error, fetch } = useAICallCampaigns();
  const { presets, fetch: fetchPresets } = useMarketingPresets();
  const [status, setStatus] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    fetch();
    fetchPresets();
  }, [fetch, fetchPresets]);
  const anyRunning = campaigns.some((c) => c.status === "running");
  useInterval(() => fetch(), 30000, anyRunning);

  const list = useMemo(() => (status ? campaigns.filter((c) => c.status === status) : campaigns), [campaigns, status]);

  const onSaved = (campaign) => {
    setFormOpen(false);
    if (campaign?._id) navigate(`/ai-calling/campaigns/${campaign._id}`);
    else fetch();
  };

  return (
    <AICallingPage
      title="AI Call Campaigns"
      subtitle="Pick who the AI agent calls, then launch"
      actions={
        <>
          <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>New campaign</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => fetch()} disabled={loading}>Refresh</Button>
        </>
      }
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(CAMPAIGN_STATUS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {formatNumber(list.length)} campaign{list.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      {loading && !campaigns.length ? (
        <Loading text="Loading campaigns..." />
      ) : error && !campaigns.length ? (
        <ErrorState error={error} onRetry={() => fetch()} />
      ) : list.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" gutterBottom>{status ? "No campaigns with this status." : "No AI call campaigns yet."}</Typography>
          {!status && <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>New campaign</Button>}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell align="right">Queued / holdout</TableCell>
                <TableCell align="right">Dialed / answered</TableCell>
                <TableCell align="right">Link sent</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((c) => {
                const s = c.stats || {};
                const smsCost = Number(s.smsCostBdt) || 0;
                return (
                  <TableRow key={c._id} hover className="aic-row-link" onClick={() => navigate(`/ai-calling/campaigns/${c._id}`)}>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={c.name}>{c.name}</Typography>
                      <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                        {c.system === "ladder" && <Chip size="small" variant="outlined" label="Channel ladder" />}
                        {c.system === "notice" && <Chip size="small" variant="outlined" label="Purchase notice" />}
                        {c.system === "signup" && <Chip size="small" variant="outlined" label="New signups" />}
                        {c.pilot?.enabled && <Chip size="small" variant="outlined" color="secondary" label="Pilot" />}
                      </Box>
                    </TableCell>
                    <TableCell><CampaignStatusChip status={c.status} /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{audienceText(c, presets)}</Typography>
                      {c.audience?.size > 0 && (
                        <Typography variant="caption" color="text.secondary">{formatNumber(c.audience.size)} members</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatNumber(s.queued)} / {formatNumber(s.holdout)}</TableCell>
                    <TableCell align="right">{formatNumber(s.dialed)} / {formatNumber(s.answered)}</TableCell>
                    <TableCell align="right">{formatNumber(s.linkSent)}</TableCell>
                    <TableCell align="right">
                      {formatBdt((Number(s.costBdt) || 0) + smsCost)}
                      {smsCost > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">incl. SMS {formatBdt(smsCost)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{dhaka(c.createdAt)}</Typography>
                      {c.createdByName && <Typography variant="caption" color="text.secondary">by {c.createdByName}</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AICallCampaignForm open={formOpen} campaign={null} onClose={() => setFormOpen(false)} onSaved={onSaved} />
    </AICallingPage>
  );
};

export default AICallingCampaigns;
